/**
 * Sentence splitting. Pure — no I/O, no clock, no randomness.
 *
 * The one property that matters is that offsets are exact: `text.slice(start, end)` is the
 * sentence, sentences are in order and never overlap, and everything between them is
 * whitespace the page can reproduce. The results view rebuilds the writing from these
 * offsets, so a wrong one shows the teacher a sentence that is not what they wrote.
 *
 * Jev points at state by id, so each sentence carries `S001`-style id and `tagForState`
 * lays them out one per line for the request.
 */

export interface Sentence {
  id: string;
  text: string;
  start: number;
  end: number;
}

export const MAX_SENTENCES = 120;
export const MAX_CHARS = 12_000;

/** A fragment shorter than this is not a sentence; it is glued to a neighbour. */
const MIN_SENTENCE_CHARS = 3;

/** Common abbreviations that end in a period without ending a sentence. */
const ABBREVIATIONS = new Set(['mr', 'mrs', 'ms', 'dr', 'prof', 'st', 'vs', 'e.g', 'i.e', 'etc', 'no']);

export const sentenceId = (index: number): string => `S${String(index + 1).padStart(3, '0')}`;

/** The word (letters, digits, periods) that ends at `end`, lowercased. */
function wordEndingAt(text: string, end: number): string {
  let start = end;
  while (start > 0 && /[A-Za-z0-9.]/.test(text[start - 1]!)) start -= 1;
  return text.slice(start, end).toLowerCase();
}

/** Is the token before this period a list marker such as `1.` or `a.` at the start of a line? */
function isListMarker(text: string, periodIndex: number): boolean {
  let start = periodIndex;
  while (start > 0 && /[A-Za-z0-9]/.test(text[start - 1]!)) start -= 1;
  const marker = text.slice(start, periodIndex);
  if (!/^(\d{1,2}|[A-Za-z])$/.test(marker)) return false;
  // Only at the start of a line (possibly after indentation).
  let i = start - 1;
  while (i >= 0 && (text[i] === ' ' || text[i] === '\t')) i -= 1;
  return i < 0 || text[i] === '\n';
}

/** Indexes at which a new sentence starts, not counting 0. */
function breakPoints(text: string): number[] {
  const breaks: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;

    // A blank line always ends a sentence.
    if (ch === '\n') {
      let j = i + 1;
      while (j < text.length && (text[j] === ' ' || text[j] === '\t' || text[j] === '\r')) j += 1;
      if (text[j] === '\n') {
        breaks.push(i);
        continue;
      }
    }

    if (ch !== '.' && ch !== '!' && ch !== '?') continue;

    // Swallow a run of terminal punctuation and closing quotes/brackets.
    let j = i + 1;
    while (j < text.length && /[.!?"'”’)\]]/.test(text[j]!)) j += 1;
    if (j < text.length && !/\s/.test(text[j]!)) continue;

    if (ch === '.') {
      const word = wordEndingAt(text, i).replace(/\.$/, '');
      if (ABBREVIATIONS.has(word)) { i = j - 1; continue; }
      if (isListMarker(text, i)) { i = j - 1; continue; }
    }

    breaks.push(j);
    i = j - 1;
  }
  return breaks;
}

/** Trim a [start, end) span to its non-whitespace content. */
function trimSpan(text: string, start: number, end: number): [number, number] {
  let s = start;
  let e = end;
  while (s < e && /\s/.test(text[s]!)) s += 1;
  while (e > s && /\s/.test(text[e - 1]!)) e -= 1;
  return [s, e];
}

export function splitSentences(text: string): Sentence[] {
  const spans: [number, number][] = [];
  let cursor = 0;
  for (const at of breakPoints(text)) {
    if (at <= cursor) continue;
    const [s, e] = trimSpan(text, cursor, at);
    if (e > s) spans.push([s, e]);
    cursor = at;
  }
  const [s, e] = trimSpan(text, cursor, text.length);
  if (e > s) spans.push([s, e]);

  // Glue fragments to a neighbour: the following sentence, or the previous one when last.
  const merged: [number, number][] = [];
  let pending: [number, number] | null = null;
  for (const span of spans) {
    let current: [number, number] = span;
    if (pending) { current = [pending[0], span[1]]; pending = null; }
    if (current[1] - current[0] < MIN_SENTENCE_CHARS) { pending = current; continue; }
    merged.push(current);
  }
  if (pending) {
    const last = merged.pop();
    merged.push(last ? [last[0], pending[1]] : pending);
  }

  return merged.map(([start, end], index) => ({
    id: sentenceId(index),
    text: text.slice(start, end),
    start,
    end,
  }));
}

/** The Jev state: one tagged sentence per line, so a question can point at `S007`. */
export const tagForState = (sentences: readonly Sentence[]): string =>
  sentences.map((s) => `${s.id}| ${s.text.replace(/\s*\n\s*/g, ' ')}`).join('\n');
